import React, { useState } from "react";
import { Section, SectionHeader } from "@design-system/Section/Section";
import { Tag } from "@design-system/Tag/Tag";
import { VStack } from "@design-system/VStack/VStack";
import { CheckIcon } from "@icons";
import {
    BooleanControl,
    TextControl,
    VariantGrid,
    VariantItem,
} from "./playgroundControls";
import gridStyles from "./playgroundControls.module.scss";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./structureLayoutControls.module.scss";

const includedItems = [
    "Real-time categorization",
    "Receipt matching",
    "Unlimited accounts",
    "Expert support",
];

const IncludedList = () => (
    <ul className={styles.checkList}>
        {includedItems.map((item) => (
            <li key={item}>
                <CheckIcon /> {item}
            </li>
        ))}
    </ul>
);

export const SectionPlayground = () => {
    const [heading, setHeading] = useState("What's included");
    const [subheading, setSubheading] = useState(
        "Everything in the flat monthly plan.",
    );
    const [hasHeading, setHasHeading] = useState(true);
    const [hasSubheading, setHasSubheading] = useState(true);
    const [isHeadingNode, setIsHeadingNode] = useState(false);

    const headingNode = (
        <h4 className={styles.sectionHeadingNode}>
            {heading}
            <Tag size="sm" color="neutral">
                Beta
            </Tag>
        </h4>
    );
    const headingContent = isHeadingNode ? headingNode : heading;

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Content</h2>
                        <div className={styles.fields}>
                            <TextControl
                                label="Heading"
                                value={heading}
                                onChange={setHeading}
                            />
                            <TextControl
                                label="Subheading"
                                value={subheading}
                                onChange={setSubheading}
                            />
                        </div>
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Heading"
                                checked={hasHeading}
                                onChange={setHasHeading}
                            />
                            <BooleanControl
                                label="Subheading"
                                checked={hasSubheading}
                                onChange={setHasSubheading}
                            />
                            <BooleanControl
                                label="Heading as node"
                                checked={isHeadingNode}
                                onChange={setIsHeadingNode}
                            />
                        </div>
                        <p className={styles.hint}>
                            A string heading is wrapped in an <code>h4</code>{" "}
                            for you; a node heading is rendered as-is, which is
                            how callers add badges or actions beside the title.
                        </p>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={hasHeading ? "with heading" : "no heading"}
                width="wide"
                output={JSON.stringify(
                    {
                        componentProps: {
                            heading: hasHeading ? heading : null,
                            headingType: isHeadingNode ? "node" : "string",
                            subheading: hasSubheading ? subheading : null,
                        },
                        showcaseState: {},
                    },
                    null,
                    2,
                )}
            >
                <VStack gap={12} className={gridStyles.fillWidth}>
                    <div className={styles.sectionSurface}>
                        <Section
                            heading={hasHeading ? headingContent : undefined}
                            subheading={hasSubheading ? subheading : undefined}
                        >
                            <IncludedList />
                        </Section>
                    </div>
                    <VariantGrid title="Header combinations" columns="full">
                        <VariantItem label="heading">
                            <div className={styles.sectionSurface}>
                                <Section heading="Account details">
                                    <IncludedList />
                                </Section>
                            </div>
                        </VariantItem>
                        <VariantItem label="heading + subheading">
                            <div className={styles.sectionSurface}>
                                <Section
                                    heading="Account details"
                                    subheading="Used on customer invoices."
                                >
                                    <IncludedList />
                                </Section>
                            </div>
                        </VariantItem>
                        <VariantItem label="content only">
                            <div className={styles.sectionSurface}>
                                <Section>
                                    <IncludedList />
                                </Section>
                            </div>
                        </VariantItem>
                    </VariantGrid>
                    <VariantGrid
                        title="SectionHeader on its own"
                        columns="full"
                    >
                        <VariantItem label="SectionHeader">
                            <div className={styles.sectionSurface}>
                                <SectionHeader>
                                    <h4>Business information</h4>
                                </SectionHeader>
                            </div>
                        </VariantItem>
                    </VariantGrid>
                </VStack>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
